"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const vue_1 = require("vue");
const use_file_validation_1 = require("./use-file-validation");
function useValidationText(elementRef, displayName, options, externalErrorMessage, requiredErrorMessage) {
    const { isFileSizeError, isFileTypeError, getFormattedAcceptString } = (0, use_file_validation_1.useFileValidation)(elementRef, options);
    const numberOfSubmits = (0, vue_1.inject)("numberOfSubmits", (0, vue_1.ref)(0));
    const formValidationResetControl = (0, vue_1.inject)("formValidationResetControl", (0, vue_1.ref)(0));
    const hasFormWrapper = (0, vue_1.inject)("hasFormWrapper", false);
    const updateFormValidity = (0, vue_1.inject)("updateFormValidity", () => { });
    const requiredError = (0, vue_1.ref)(false);
    const patternError = (0, vue_1.ref)(false);
    const typeError = (0, vue_1.ref)(false);
    const fileTypeError = (0, vue_1.ref)(false);
    const fileSizeError = (0, vue_1.ref)(false);
    const rangeOverflowError = (0, vue_1.ref)(false);
    const rangeUnderflowError = (0, vue_1.ref)(false);
    const tooLongError = (0, vue_1.ref)(false);
    const tooShortError = (0, vue_1.ref)(false);
    const badInputError = (0, vue_1.ref)(false);
    const isLiveValidation = (0, vue_1.ref)(false);
    const isValidationVisible = (0, vue_1.computed)(() => {
        const hasExternalErrorMessage = !!(externalErrorMessage === null || externalErrorMessage === void 0 ? void 0 : externalErrorMessage.value);
        const hasInternalErrorMessage = !!errorMessage.value;
        const wasSubmitted = numberOfSubmits.value > 0;
        const internalValidationActive = isLiveValidation.value || wasSubmitted;
        return hasExternalErrorMessage || (hasInternalErrorMessage && internalValidationActive);
    });
    // Helfer für initialLiveValidation, damit bei Validation Reset (formValidationResetControl) die Validierung nicht kurz "aufblitzt"
    let initialLiveValidationTimeout = 0;
    const updateValidateStates = () => {
        var _a;
        if (!elementRef.value)
            return;
        // patternMissmatch hat höhere Priorität wie typeMismatch (z.B. wichtig bei E-Mail-Pattern)
        const hasPattern = (_a = elementRef.value) === null || _a === void 0 ? void 0 : _a.getAttribute("pattern");
        requiredError.value = elementRef.value.validity.valueMissing;
        rangeOverflowError.value = elementRef.value.validity.rangeOverflow;
        rangeUnderflowError.value = elementRef.value.validity.rangeUnderflow;
        tooLongError.value = elementRef.value.validity.tooLong;
        tooShortError.value = elementRef.value.validity.tooShort;
        badInputError.value = elementRef.value.validity.badInput;
        patternError.value = elementRef.value.validity.patternMismatch;
        typeError.value = hasPattern ? false : elementRef.value.validity.typeMismatch;
        fileTypeError.value = isFileTypeError();
        fileSizeError.value = isFileSizeError();
    };
    // eslint-disable-next-line complexity
    const errorMessage = (0, vue_1.computed)(() => {
        var _a, _b;
        if (externalErrorMessage === null || externalErrorMessage === void 0 ? void 0 : externalErrorMessage.value)
            return externalErrorMessage === null || externalErrorMessage === void 0 ? void 0 : externalErrorMessage.value;
        if (requiredError.value)
            return requiredErrorMessage || `${displayName} ist erforderlich`;
        if (patternError.value)
            return (options === null || options === void 0 ? void 0 : options.patternErrorMessage) || `${displayName} ist ungültig`;
        if (typeError.value)
            return `${displayName} ist ungültig`;
        if (fileTypeError.value)
            return `Dateiformat nicht erlaubt. Erlaubt sind ${getFormattedAcceptString()}`;
        if (fileSizeError.value)
            return `Die File ist zu groß. Erlaubt sind bis zu ${options === null || options === void 0 ? void 0 : options.maxFileSizeMb} MB`;
        if (rangeOverflowError.value)
            return ((_a = elementRef === null || elementRef === void 0 ? void 0 : elementRef.value) === null || _a === void 0 ? void 0 : _a.type) === "date"
                ? `${displayName} liegt nicht weit genug zurück`
                : `${displayName} ist zu groß`;
        if (rangeUnderflowError.value)
            return ((_b = elementRef === null || elementRef === void 0 ? void 0 : elementRef.value) === null || _b === void 0 ? void 0 : _b.type) === "date"
                ? `${displayName} ist zu weit in der Vergangenheit`
                : `${displayName} ist zu klein`;
        if (tooLongError.value)
            return `${displayName} ist zu lang`;
        if (tooShortError.value)
            return `${displayName} ist zu kurz`;
        if (badInputError.value)
            return `${displayName} ist ungültig`;
        return undefined;
    });
    // Update errorMessage on the input
    (0, vue_1.watch)(() => errorMessage.value, () => {
        if (!elementRef.value)
            return;
        elementRef.value.errorMessage = errorMessage.value;
        const newValidity = errorMessage.value || "";
        elementRef.value.setCustomValidity(newValidity);
    });
    (0, vue_1.watch)(() => numberOfSubmits.value, (isSubmitTriggered) => {
        if (isSubmitTriggered)
            updateValidateStates();
    });
    (0, vue_1.watch)(() => formValidationResetControl.value, () => {
        window.clearTimeout(initialLiveValidationTimeout);
        initialLiveValidationTimeout = 0;
        isLiveValidation.value = false;
    });
    (0, vue_1.watch)(() => isValidationVisible.value, () => {
        (0, vue_1.nextTick)(updateFormValidity); // next tick to before update the computed error message
    });
    const handleInput = () => (0, vue_1.nextTick)(updateValidateStates);
    const handleChange = (startLiveValidation) => {
        updateValidateStates();
        if ((startLiveValidation || !hasFormWrapper) && errorMessage.value) {
            doLiveValidation();
        }
    };
    const handleBlur = handleChange;
    const doLiveValidation = () => {
        // delay live validation to prevent flickering on login layer/landing pages (switching between login and register)
        if (!initialLiveValidationTimeout && !isLiveValidation.value) {
            initialLiveValidationTimeout = window.setTimeout(() => {
                isLiveValidation.value = true;
            }, 180);
        }
    };
    return {
        errorMessage,
        handleChange,
        handleBlur,
        handleInput,
        isValidationVisible,
    };
}
exports.default = useValidationText;
